/**
 * Sign-in, sign-up and credential changes. Split out of `lib/firebase.js`
 * on 16 Aug 2026 because that file had reached exactly 400 lines — the hard
 * limit in `scripts/check.mjs` — and step 4.2 had to add the Firestore cache
 * configuration and batched writes to it.
 *
 * ARCHITECTURE.md §3 says the Firebase SDK is touched in ONE place. This is
 * still one place: `firebase.js` re-exports every function below, so no
 * importer changed and no feature imports this file directly. Think of the
 * pair as one module in two files — "boot and data" and "who you are".
 *
 * The auth INSTANCE is not created here. `firebase.js` owns the lifecycle and
 * hands it over with `bindAuth()` during `initFirebase()`. The dependency
 * therefore points one way only (firebase.js -> firebase-auth.js), because a
 * cycle between the two would work today and break the first time someone
 * reordered a statement.
 */

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut as fbSignOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  EmailAuthProvider,
} from 'firebase/auth'

/** @type {any} */
let auth = null

/**
 * Hand this module the Auth instance. Called once, by `initFirebase()`.
 * @param {any} instance
 */
export function bindAuth(instance) {
  auth = instance
}

/**
 * Every function below needs a bound instance. Without this guard a
 * signed-out caller reached the SDK with `null` and got
 * "Cannot read properties of null", which says nothing about the cause.
 * @returns {any}
 */
function requireAuth() {
  if (!auth) throw new Error('Auth is not ready. Await initFirebase() first.')
  return auth
}

/** @param {string} email @param {string} password */
export function signIn(email, password) {
  return signInWithEmailAndPassword(requireAuth(), email, password)
}

/** @param {string} email @param {string} password */
export function signUp(email, password) {
  return createUserWithEmailAndPassword(requireAuth(), email, password)
}

export function signInWithGoogle() {
  return signInWithPopup(requireAuth(), new GoogleAuthProvider())
}

export function signOut() {
  return fbSignOut(requireAuth())
}

/** @param {string} email */
export function resetPassword(email) {
  return sendPasswordResetEmail(requireAuth(), email)
}

/**
 * Full-page redirect sign-in. The fallback when a browser blocks the popup.
 */
export function signInWithGoogleRedirect() {
  return signInWithRedirect(requireAuth(), new GoogleAuthProvider())
}

/**
 * Collect the result of a redirect sign-in after the page comes back.
 * Returns null when this load was not a redirect return, which is the
 * overwhelmingly common case.
 *
 * @returns {Promise<any|null>}
 */
export async function consumeRedirectResult() {
  try {
    const result = await getRedirectResult(requireAuth())
    return result ? result.user : null
  } catch (err) {
    console.error('redirect sign-in failed', err)
    return null
  }
}

/**
 * Ask Firebase to send the address-confirmation email.
 *
 * Deliberately never throws: a new account must not appear to have failed
 * just because the verification mail could not go out. The account exists
 * either way, and the banner will keep prompting.
 *
 * @param {any} user
 * @returns {Promise<boolean>} whether it was sent
 */
export async function sendVerificationEmail(user) {
  try {
    await sendEmailVerification(user)
    return true
  } catch (err) {
    console.error('verification email failed to send', err)
    return false
  }
}

/**
 * @param {any} user
 * @param {string} displayName
 */
export function setDisplayName(user, displayName) {
  return updateProfile(user, { displayName })
}

/**
 * Whether this account has a password at all.
 *
 * Google-only accounts do not, and the legacy screen showed them three
 * password boxes anyway — filling them in produced an "auth/invalid-credential"
 * error that told the advisor nothing about why.
 *
 * @param {any} user
 * @returns {boolean}
 */
export function hasPasswordProvider(user) {
  const providers = (user && user.providerData) || []
  return providers.some((/** @type {any} */ p) => p && p.providerId === 'password')
}

/**
 * Change the signed-in user's password.
 *
 * Firebase requires a recent sign-in before it will accept this, so the
 * current password is re-submitted as a credential first. Both steps live
 * here rather than in the feature, because both touch the SDK.
 *
 * @param {any} user
 * @param {string} currentPassword
 * @param {string} newPassword
 * @returns {Promise<void>}
 */
export async function changePassword(user, currentPassword, newPassword) {
  if (!user || !user.email) throw new Error('Not signed in.')
  const credential = EmailAuthProvider.credential(user.email, currentPassword)
  await reauthenticateWithCredential(user, credential)
  await updatePassword(user, newPassword)
}

/**
 * Prove, again, that the person at the keyboard is the account holder.
 *
 * `revealCardAuth` refuses to hand over a card unless the caller authenticated
 * in the last ten minutes, so a warm session on a stolen laptop is worth
 * nothing. Google accounts re-prove with a popup; password accounts with the
 * password, which the caller collects — this module never opens a prompt of
 * its own, because a `window.prompt` inside a library is untestable and
 * unstyleable.
 *
 * The forced token refresh at the end is the part that actually matters: the
 * backend reads `auth_time` off the ID token, and without a refresh the next
 * call still carries the old one and fails again for no visible reason.
 *
 * @param {any} user
 * @param {string} [password] - required for password accounts
 * @returns {Promise<void>}
 */
export async function reauthenticate(user, password) {
  if (!user) throw new Error('Please sign in again.')

  if (hasPasswordProvider(user)) {
    if (!password) throw new Error('Re-authentication canceled.')
    await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password))
  } else {
    await reauthenticateWithPopup(user, new GoogleAuthProvider())
  }

  await user.getIdToken(true)
}

/**
 * Wait until this account's ID token can actually be used.
 *
 * `onAuthStateChanged` reports a user as soon as one EXISTS, which for an
 * account created a moment ago is before its token is usable. Every read the
 * app fires in that window is evaluated against no auth and refused — ten at
 * once, on the first screen a new advisor ever sees. See `watch-retry.js` for
 * the full account of it.
 *
 * Resolves either way: a failure here is not a reason to refuse to start, and
 * the retry in `watch-retry.js` is the second line for exactly that case.
 *
 * @param {any} user
 * @returns {Promise<boolean>} whether a token was obtained
 */
export async function tokenReady(user) {
  const who = user
  if (!who || typeof who.getIdToken !== 'function') return false
  try {
    /* Cached once minted, so this costs nothing on every later sign-in — it
       only ever waits on the exchange that has not happened yet. */
    await who.getIdToken()
    return true
  } catch (err) {
    console.warn('auth token was not ready', err)
    return false
  }
}

/**
 * Mint a brand-new ID token for THIS device, discarding the cached one.
 *
 * The one thing "sign out every other device" cannot do without: the
 * revocation is account-wide, so the browser that asked for it is holding a
 * token that has just been invalidated too. Refreshing immediately is what
 * keeps the advisor signed in while every other device is cut off — without
 * it, pressing the button signs YOU out and leaves the thief's tab alive for
 * up to an hour, which is precisely backwards.
 *
 * @param {any} user
 * @returns {Promise<boolean>} whether a fresh token was obtained
 */
export async function refreshToken(user) {
  if (!user || typeof user.getIdToken !== 'function') return false
  try {
    await user.getIdToken(true)
    return true
  } catch (err) {
    console.warn('token refresh failed', err)
    return false
  }
}
